import type React from 'react';
import { Progress } from '../tailgrids/core/progress.js';

interface BudgetBarProps {
  value: number;
  max: number;
  label?: React.ReactNode;
  showPct?: boolean;
  unit?: string;
  /** Force green fill (e.g. completed tasks). Overrides the usage-based colour. */
  ok?: boolean;
}

/** Usage colour: calm until 60%, warning to 90%, danger past it. */
function barColor(pct: number, ok?: boolean): string {
  if (ok) return 'var(--color-success)';
  if (pct > 0.9) return 'var(--color-danger)';
  if (pct > 0.6) return 'var(--color-warning)';
  return 'var(--color-accent)';
}

/** How much of a budget (cost, tokens, steps) is used (NextAdmin progress bar). No budget set shows an empty track. */
export function BudgetBar({ value, max, label, showPct = false, unit, ok }: BudgetBarProps) {
  const hasBudget = max > 0;
  const pct = hasBudget ? Math.min(value / max, 1) : 0;

  return (
    <div className="w-full">
      {(label != null || showPct) && (
        <div className="mb-1.5 flex items-center justify-between gap-2 text-xs">
          {label != null && <span className="truncate text-muted">{label}</span>}
          {showPct && hasBudget && (
            <span className="shrink-0 font-semibold text-heading tabular-nums">
              {Math.round(pct * 100)}%
              {unit && (
                <span className="ml-1 font-normal text-muted">
                  ({value}
                  {unit} / {max}
                  {unit})
                </span>
              )}
            </span>
          )}
          {showPct && !hasBudget && <span className="shrink-0 text-muted">—</span>}
        </div>
      )}
      <Progress
        progress={Math.round(pct * 100)}
        className="max-w-none"
        trackColor="var(--color-surface-alt)"
        barColor={hasBudget ? barColor(pct, ok) : 'transparent'}
      />
    </div>
  );
}
